/**
 * Auth Context
 * 
 * Manages authentication state and integrates with backend API
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import * as api from './api';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Initialize auth state from localStorage
   */
  useEffect(() => {
    const initAuth = async () => {
      try {
        const storedToken = localStorage.getItem('codewave_token');
        const storedUser = localStorage.getItem('codewave_user');

        if (storedToken && storedUser) {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));

          // Verify token with backend
          try {
            const freshUser = await api.getMe();
            setUser(freshUser);
            localStorage.setItem('codewave_user', JSON.stringify(freshUser));
          } catch (err) {
            // Token invalid, clear auth
            handleLogout();
          }
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  /**
   * Login with backend API
   * @param {string} email 
   * @param {string} password 
   */
  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null);

      const { token: newToken, user: newUser } = await api.login(email, password);

      setToken(newToken);
      setUser(newUser);

      return { success: true, user: newUser };
    } catch (err) {
      setError(err.message || 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Logout (clear auth state)
   */
  const logout = () => {
    handleLogout();
  };

  /**
   * Internal logout handler
   */
  const handleLogout = () => {
    setUser(null);
    setToken(null);
    setError(null);
    localStorage.removeItem('codewave_token');
    localStorage.removeItem('codewave_user');
  };

  /**
   * Update user data
   * @param {Object} userData 
   */
  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('codewave_user', JSON.stringify(userData));
  };

  /**
   * Check if user is authenticated
   */
  const isAuthenticated = () => {
    return !!token && !!user;
  };

  /**
   * Check if user is admin
   */
  const isAdmin = () => {
    return user?.role === 'admin';
  };

  /**
   * Check if user is student
   */
  const isStudent = () => {
    return user?.role === 'student';
  };

  /**
   * Get user's track
   */
  const getUserTrack = () => {
    return user?.track || null;
  };

  /**
   * Check if user is instructor
   */
  const isInstructor = () => {
    return user?.role === 'instructor';
  };

  const value = {
    user,
    token,
    loading,
    error,
    login,
    logout,
    updateUser,
    isAuthenticated,
    isAdmin,
    isStudent,
    isInstructor,
    getUserTrack,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Custom hook to use auth context
 */
export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
}

